export type OfflineAsset = { url: string; revision: string; bytes: number }

/** Public learning media only: API responses and private learner data never enter this cache. */
export function isOfflineAsset(asset: OfflineAsset) {
  return (
    /^\/(img|images|audio)\/[\w./-]+\.(webp|png|jpg|jpeg|svg|mp3|wav)$/.test(
      asset.url,
    ) &&
    !asset.url.split('/').includes('..') &&
    typeof asset.revision === 'string' &&
    Number.isFinite(asset.bytes) &&
    asset.bytes > 0
  )
}

export async function downloadOfflineAssets(
  assets: OfflineAsset[],
  cache: Pick<Cache, 'match' | 'put'>,
  revisions: Pick<Cache, 'match' | 'put'>,
  fetchAsset: (url: string) => Promise<Response>,
  yieldToPage: () => Promise<void>,
  signal: AbortSignal,
) {
  let complete = true
  for (const asset of assets) {
    if (signal.aborted) return false
    if (!isOfflineAsset(asset)) continue
    const existing = await cache.match(asset.url)
    const revision = await revisions.match(asset.url)
    if (existing && revision && (await revision.text()) === asset.revision)
      continue

    await yieldToPage()
    if (signal.aborted) return false
    const response = await fetchAsset(asset.url)
    // Never save errors, redirects, or partial audio responses as complete assets.
    if (response.status !== 200 || response.redirected) {
      complete = false
      continue
    }
    if (signal.aborted) return false
    await cache.put(asset.url, response)
    await revisions.put(asset.url, new Response(asset.revision))
  }
  return complete
}
